import type { CalendarSlot, PreviewCalendarData } from "./preview-calendar";

/** Only departures the provider currently marks bookable can light a destination. */
export function bookableDeparture(calendar: PreviewCalendarData | undefined, now = new Date()): CalendarSlot | undefined {
  if (!calendar) return;
  const today = new Intl.DateTimeFormat("en-CA", { timeZone: calendar.timeZone || "America/Cancun", year: "numeric", month: "2-digit", day: "2-digit" }).format(now);
  return calendar.slots.filter(slot => slot.date >= today && slot.available && !slot.soldOut && (slot.unlimited || (slot.capacity ?? 0) > 0))
    .sort((a, b) => a.date.localeCompare(b.date) || (a.startTime ?? "").localeCompare(b.startTime ?? ""))[0];
}

export function adultReferenceItems(calendar: PreviewCalendarData, slot: CalendarSlot) {
  const adult = calendar.categories.find(category => category.ticketCategory === "ADULT" || /adult/i.test(category.title));
  if (!adult || calendar.perBooking || slot.minParticipants > 1) return null;
  const price = slot.prices.find(price => price.id === adult.id);
  if (!price || (price.minRequired ?? 1) > 1) return null;
  return [{ pricingCategoryId: adult.id, quantity: 1 }];
}
export type MapPoint = { x: number; y: number };
export type MapView = { s: number; tx: number; ty: number };

/** Fits map-space points (1000x580 projection) inside a w×h pixel box. */
export function fitView(points: MapPoint[], w: number, h: number, pad = 0.14, minSpan = 40): MapView {
  if (!points.length || w <= 0 || h <= 0) return { s: 1, tx: 0, ty: 0 };
  let x0 = Math.min(...points.map(p => p.x)), x1 = Math.max(...points.map(p => p.x));
  let y0 = Math.min(...points.map(p => p.y)), y1 = Math.max(...points.map(p => p.y));
  if (x1 - x0 < minSpan) { const c = (x0 + x1) / 2; x0 = c - minSpan / 2; x1 = c + minSpan / 2; }
  if (y1 - y0 < minSpan) { const c = (y0 + y1) / 2; y0 = c - minSpan / 2; y1 = c + minSpan / 2; }
  const s = Math.min((w * (1 - 2 * pad)) / (x1 - x0), (h * (1 - 2 * pad)) / (y1 - y0));
  return { s, tx: w / 2 - s * (x0 + x1) / 2, ty: h / 2 - s * (y0 + y1) / 2 };
}

/** Region around the active point: neighbours within `radius` map units. */
export function regionPoints(active: MapPoint, all: MapPoint[], radius = 120): MapPoint[] {
  return all.filter(p => Math.hypot(p.x - active.x, p.y - active.y) <= radius);
}

export type LabelItem = { id: string; x: number; y: number; w: number; h?: number };
export type PlacedLabel = { id: string; x: number; y: number; anchor: "start" | "end" | "middle"; box: [number, number, number, number] };

/** Greedy non-overlapping label placement in screen pixels; items earlier in the list win. */
export function layoutLabels(items: LabelItem[], width: number, height: number, gap = 9, obstacles: PlacedLabel["box"][] = []): PlacedLabel[] {
  const placed: PlacedLabel[] = [];
  const hit = (a: PlacedLabel["box"], b: PlacedLabel["box"]) => a[0] < b[2] && a[2] > b[0] && a[1] < b[3] && a[3] > b[1];
  for (const it of items) {
    const h = it.h ?? 16;
    const options: Array<Omit<PlacedLabel, "id">> = [
      { x: it.x + gap, y: it.y + 5, anchor: "start", box: [it.x + gap, it.y - h / 2, it.x + gap + it.w, it.y + h / 2] },
      { x: it.x - gap, y: it.y + 5, anchor: "end", box: [it.x - gap - it.w, it.y - h / 2, it.x - gap, it.y + h / 2] },
      { x: it.x, y: it.y - gap - 2, anchor: "middle", box: [it.x - it.w / 2, it.y - gap - h, it.x + it.w / 2, it.y - gap] },
      { x: it.x, y: it.y + gap + 12, anchor: "middle", box: [it.x - it.w / 2, it.y + gap, it.x + it.w / 2, it.y + gap + h] },
    ];
    if (obstacles.length) for (const dy of [-36, 36, -60, 60]) for (const dx of [0, -32, 32]) {
      options.push({x:it.x+dx,y:it.y+dy+5,anchor:"middle",box:[it.x+dx-it.w/2,it.y+dy-h/2,it.x+dx+it.w/2,it.y+dy+h/2]});
    }
    const fit = options.find(o => o.box[0] >= 2 && o.box[2] <= width - 2 && o.box[1] >= 2 && o.box[3] <= height - 2 && !placed.some(p => hit([p.box[0]-3,p.box[1]-3,p.box[2]+3,p.box[3]+3], o.box)) && !obstacles.some(b => hit(b, o.box)));
    if (fit) placed.push({ id: it.id, ...fit });
  }
  return placed;
}

export type QuickDate = "today" | "tomorrow" | "week" | { date: string };

function addDays(iso: string, n: number) {
  const d = new Date(`${iso}T12:00:00Z`); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10);
}

/** Inclusive [from, to] ISO range for a quick-date chip. */
export function quickDateRange(q: QuickDate, today: string): [string, string] {
  if (q === "today") return [today, today];
  if (q === "tomorrow") { const t = addDays(today, 1); return [t, t]; }
  if (q === "week") return [today, addDays(today, 6)];
  return [q.date, q.date];
}

/** Bookable departures inside a date range (same rules as bookableDeparture). */
export function bookableInRange(slots: CalendarSlot[], [from, to]: [string, string]): CalendarSlot[] {
  return slots.filter(s => s.date >= from && s.date <= to && s.available && !s.soldOut && (s.unlimited || (s.capacity ?? 0) > 0))
    .sort((a, b) => a.date.localeCompare(b.date) || (a.startTime ?? "").localeCompare(b.startTime ?? ""));
}

const foldName = (s: string) => s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/\s+/g, " ").trim();
/** Real provider pickup place matching the chosen hotel by name; never inferred. */
export function matchingPickup<P extends { title: string }>(places: P[] | undefined, hotel: string): P | undefined {
  if (!hotel || !places?.length) return;
  const key = foldName(hotel);
  return places.find(p => foldName(p.title) === key);
}
